import type { CommonStylesProps } from "@/(admin)/modules/ui-builder/config/lib/commonCSSProps"
import { BlockContent } from "@/lib/config/types"
import { SiteId } from "@/sites/config"
import { FormInstance, FormProps, TablePaginationConfig } from "antd"
import { Rule } from "rc-field-form/es/interface"
import { ValidateErrorEntity } from "rc-field-form/lib/interface"
import { ChangeEvent } from "react"

export enum ReceiveSMS {
  NO = "no",
  CS = "cz",
  VN = "vn",
  EN = "en",
}

export interface LoginResponse {
  jwt: string
  user: {
    id: number
    email: string
    username: string
    provider: string
    confirmed: boolean
    blocked: boolean
    createdAt: string
    updatedAt: string
  }
}
// slice redux
export interface LoginState {
  isAuthenticated: boolean
  user: {
    id: number
    email: string
    username: string
    provider: string
    confirmed: boolean
    blocked: boolean
    createdAt: string
    updatedAt: string
  } | null
  accessToken: string | null
  permissions: {}
}
export interface PostResponse {
  data: PostData[]
  error?: string | null
  meta: Meta
}

export interface PostListProps {
  dataItem?: string
}

export interface PageListProps {
  searchTerm?: string
}
export interface BaseListProps {
  searchTerm?: string
}

export interface dataPayloadPage {
  name: string | undefined
  content: string
  slug?: string
}
export interface dataPayloadBase {
  name: string | undefined
  slug?: string
}

export interface LiveNewsItemData {
  id?: number
  title: string
  date: string
  detail: string
}

export interface LiveNewsItemProps {
  index: number
  data?: LiveNewsItemData
  onChange?: (data: LiveNewsItemData) => void
  onDelete?: () => void
}

export interface FieldType {
  name?: string
  title?: string
  slug?: string
  password?: string
  remember?: string
  content?: string
  excerpt?: string
  description?: string
  readingTime?: number
  shortDescription?: string
  categories?: number[]
  tags?: number[]
  campaign?: number | null
  promo_video_urls?: string[]
  image?: number | null
  isLiveNews?: boolean
  isLiveHappening?: boolean
  liveNewsItem?: LiveNewsItemData[]
  metaTitle?: string
  metaDescription?: string
  keywords?: string
  metaImage?: MetaImage
  metaRobots?: string
  metaViewport?: string
  structuredData?: JSON
  canonicalURL?: string
  hotline?: string
  workingTime?: string
  address?: string
  latitude?: string
  longitude?: string
  linkMap?: string
  linkPost?: string
  email?: string
  publishedAt?: string
}

export interface PostFormProps {
  form: FormInstance
  onFinishFailed: (errorInfo: ValidateErrorEntity) => void
  onValidationErrorsChange?: (errors: { fieldName: string; message: string }[]) => void
  /** Không dùng — PostForm đọc/ghi nội dung qua field "content" của form. */
  quillValue?: string
  setQuillValue?: (value: string) => void
  onFinish: (values: FieldType) => void
  onChangeMetaImage: (value: MediaData) => void
  initialLiveNewsItems?: LiveNewsItemData[]
  initialIsLiveNews?: boolean
  onChangeContent?: () => void
  onValuesChange?: () => void
  onEditorReadyCb?: () => void
  /** The slug that was saved on the server for this post. Used to skip self-collision checks. */
  initialSlug?: string
}

export interface RecruitmentFormProps {
  form: FormInstance
  onFinishFailed: (errorInfo: ValidateErrorEntity) => void
  quillValue: string
  onFinish: (values: AttributesRecruitment) => void
}

export interface PageFormProps {
  page?: PageData
  edit?: boolean
  form: FormInstance
  onFinishFailed: (errorInfo: ValidateErrorEntity) => void
  quillValue: string
  setQuillValue: (value: string) => void
  onFinish: (values: FieldType) => void
  onChangeMetaImage: (value: MediaData) => void
  isUIBuilderPage?: boolean
}

export interface BaseFormProps {
  form: any
  onFinish: (values: any) => void
  onFinishFailed: (errorInfo: any) => void
  quillValue: string
  setQuillValue: (value: string) => void
  onChangeMetaImage: (media: MediaData | null) => void
  isUIBuilderPage: boolean
  edit?: boolean
  base: {
    name: string
    email: string
    hotline: string
    workingTime: string
    address: string
    latitude: string
    longitude: string
    linkMap: string
    linkPost: string
    publishedAt: string | null
    image: MediaData | null
    isUseUIBuilder: boolean
    locale: string
  }
}

export interface CategoryOption {
  id: number
  label: string | undefined
  value: number
  disabled: boolean
  status?: string | undefined
}

export interface CategoriesProps {
  selectedCategories: Array<string | number>
  onChangeCategories: (checkedValues: Array<string | number>) => void
  selectedCategoryOptions?: CategoryOption[]
  locale?: string
}

export interface TagOption {
  id: number
  label: string | undefined
  value: number
  disabled: boolean
}

export interface TagsProps {
  selectedTags: string[]
  onChangeTags: (checkedValues: string[]) => void
  locale?: string
}

export interface PublishProps {
  onSubmit: () => void
  onPreview?: () => void
  onView?: () => void
  status: string
  setStatus: (status: string) => void
  language: string
  setLanguages: (language: string) => void
  setPublishDate: (date: string | null) => void
  isCreateEdit?: boolean
  type?: "create" | "edit" | "translate"
  loading?: boolean
  onPageTypeChanged?: (isUseUIBuiler: boolean) => void
  onCancle?: () => void
}

export interface InputSearchProps {
  handleSearchChange: (event: ChangeEvent<HTMLInputElement>) => void
}

export interface PostData {
  id: number
  attributes: AttributesPost
}
export interface AttributesPost {
  name: string
  title: string
  slug: string
  content: string
  excerpt: string
  description: string
  readingTime?: number
  shortDescription?: string
  createdAt: string
  updatedAt: string
  publishedAt: string
  locale: string
  views: number
  old_id: number | null
  reviewStatus?: "draft" | "pending_review" | "approved" | "rejected"
  rejectReason?: string
  isLiveHappening: boolean | null
  status?: PostWorkflowStatus
  scheduledAt?: string | null
  unpublishAt?: string | null
  isLiveNews: boolean | null
  liveNewsItem: LiveNewsItemData[]
  promo_video_urls?: string[]
  isUseUIBuilder?: boolean
  author?: {
    data?: {
      id: number
      attributes: UserAttributes
    }
  }
  blockContent: {
    data?: BlockContent | null
  }
  image: {
    data?: DataImage | null
  }
  cover?: {
    data?: DataImage | null
  }
  categories: {
    data: CategoryData[]
  }
  tags: {
    data: TagData[]
  }
  seo: SeoData
  localizations?: {
    data: PostData[]
  }
  websites?: {
    data: {
      id: number
      attributes: {
        domain: string
        name?: string
        createdAt?: string
        updatedAt?: string
        publishedAt?: string
      }
    }[]
  }
  campaign?: {
    data?: {
      id?: number
      attributes?: {
        name?: string
      }
    }
  }
}

export interface AttributesSubPost {
  name?: string
  excerpt?: string
  title: string
  slug: string
  content: string
  description: string
  createdAt: string
  updatedAt: string
  publishedAt: string
  locale: string
  views: number
  old_id: number | null
  reviewStatus?: "draft" | "pending_review" | "approved" | "rejected"
  rejectReason?: string
  status?: PostWorkflowStatus
  scheduledAt?: string | null
  unpublishAt?: string | null
  isLiveHappening: boolean | null
  isLiveNews: boolean | null
  liveNewsItem: LiveNewsItemData[]
  isUseUIBuilder?: boolean
  author?: {
    data?: {
      id: number
      attributes: UserAttributes
    }
  }
  blockContent: {
    data?: BlockContent | null
  }
  cover: {
    data?: DataImage | null
  }
  categories: {
    data: CategoryData[]
  }
  tags: {
    data: TagData[]
  }
  seo: SeoData
  readingTime?: number
  shortDescription?: string
}

export interface PostSubData {
  id: number
  attributes: AttributesSubPost
}

export interface PageData {
  id: number
  attributes: AttributesPage
}
export interface BaseDataProps {
  id: number
  attributes: AttributesPost
}
export interface AttributesPage {
  name: string
  slug: string
  content: string
  createdAt: string
  updatedAt: string
  publishedAt: string
  image: {
    data?: DataImage | null
  }
}

export interface VideoAttributes {
  title: string
  url?: string
  youtubeUrl: string
  createdAt: string
  videoPublishedAt: string
  updatedAt: string
  publishedAt: string
  order?: number
  videoId?: string
  channelId?: string
  channelTitle?: string
  playlistId?: string
  syncedAt?: string
  description?: string
  image?: { data?: DataImage | null }
  cover?: { data?: DataImage | null }
  categories?: { data: CategoryData[] }
}

export interface VideoData {
  id: number
  attributes: VideoAttributes
}

export interface VideoListResponse {
  data: VideoData[]
  meta?: PaginationMeta
}
export interface AttributesBase {
  name: string
  slug: string
  content: string
  createdAt: string
  updatedAt: string
  publishedAt: string
  image: {
    data?: DataImage | null
  }
}
export interface BaseData {
  id?: number
  attributes?: {
    blockContent: any
    isUseUIBuilder: boolean
    slug: string
  }
  name: string
  email: string
  hotline: string
  workingTime: string
  address: string
  latitude: string
  longitude: string
  linkMap: string
  linkPost: string
  publishedAt: string | null
  image: MediaData | null
  isUseUIBuilder: boolean
  locale: string
}

export interface AttributesPage {
  name: string
  slug: string
  content: string
  domain?: string
  createdAt: string
  updatedAt: string
  publishedAt: string
  image: {
    data?: DataImage | null
  }
  seo: SeoData
  isUseUIBuilder?: boolean
  blockContent: { data: BlockContentData }
}
export interface PageResponse {
  data: PageData[]
  error?: string
}

export interface PageAttributes {
  id: number
  title: string
  slug: string
  content: string
  attributes: {
    name: string
    createdAt: string
    updatedAt: string
  }
  number: string
}
export interface UserAttributes {
  id: number
  username: string
  email: string
  blocked: boolean
  confirmed: boolean
  createdAt: string
  updatedAt: string
  provider: string
  role: {
    id: number
    name: string
    description: string
    type: string
    createdAt: string
  }
}

export interface Image {
  data: DataImage
}

export interface DataImage {
  id: number
  attributes: AttributesImage
}

export interface SeoData {
  id: number
  metaTitle: string
  metaDescription: string
  keywords: string
  metaRobots: string
  metaViewport: string
  structuredData: JSON
  canonicalURL: string
}

export interface AttributesImage {
  name: string
  width: number
  height: number
  formats: Formats
  hash: string
  ext: string
  mime: string
  size: number
  url: string
  provider: string
  createdAt: string
  updatedAt: string
}

export interface Meta {
  pagination: Pagination
}

export interface Pagination {
  page?: number
  pageSize?: number | undefined
  pageCount?: number | undefined
  current: number | undefined
  total: number | undefined
}
export type CategoryData = {
  id: number
  attributes: CategoryAttributes
}

export type CategoryAttributes = {
  name: string
  slug?: string
  createdAt: string
  updatedAt: string
  publishedAt: string
  description: string
  domain?: string
  type?: "post" | "video" | ""
  playlistUrl?: string
  seo?: SeoData
}

export type TagAttributes = {
  name: string
  slug?: string
  description?: string
  domain?: string
  locale?: string
  createdAt?: string
  updatedAt?: string
  publishedAt?: string | null
  posts?: {
    data?: PostData[]
  }
}

export type TagData = {
  id: number
  attributes: TagAttributes
}

export type PageEditProps = {
  id: number
  something: TagAttributes
}

export interface FormValues {
  pagination: Pagination
  createdAt: string
  updatedAt: string
  publishedAt: string
  categories: {
    data: CategoryResponse[]
  }
  tags: {
    data: TagData[]
  }
  dataItem: PostData[]
  loading: boolean
  error: string | null
}

export interface CategoryApiResponse {
  data: CategoryData[]
  meta: {
    pagination: {
      page: number
      total: number
    }
  }
}

export interface TagApiResponse {
  data: TagData[]
  meta: {
    pagination: {
      page: number
      total: number
    }
  }
}

export type TranslationAttributes = {
  key: string
  vi: string
  en?: string
  cs?: string
  website?: string
  createdAt?: string
  updatedAt?: string
}

export type TranslationData = {
  id: number
  attributes: TranslationAttributes
}

export interface MediaData {
  id: number
  name: string
  alternativeText: string
  caption: string
  width: number
  height: number
  formats: Formats
  hash: string
  ext: string
  mime: string
  size: number
  url: string
  previewUrl: string
  provider: string
  provider_metadata: string
  createdAt: string
  updatedAt: string
  attributes: AttributesImage
}

export interface ImageFormats {
  name: string
  hash: string
  ext: string
  mime: string
  path: string | null
  width: number
  height: number
  size: number
  sizeInBytes: number
  url: string
}

export interface MetaImageAttributes {
  name: string
  alternativeText: string | null
  caption: string | null
  width: number
  height: number
  formats: {
    medium: ImageFormats
    thumbnail: ImageFormats
    small: ImageFormats
  }
  hash: string
  ext: string
  mime: string
  size: number
  url: string
  previewUrl: string | null
  provider: string
  provider_metadata: any | null
  createdAt: string
  updatedAt: string
}

export interface MetaImage {
  id: number
  attributes: MetaImageAttributes
}

export interface FileInfo {
  file: {
    status: string
    url?: string
    originFileObj?: File
  }
}
export interface SEOAttributes {
  metaTitle: string
  metaDescription: string
  metaKeywords: string
  createdAt: string
  updatedAt: string
  publishedAt: string
  MetaImage: {
    data: MetaImage
  }
}

export interface SEOData {
  id: number
  attributes: SEOAttributes
}

export interface SEOResponse {
  data: SEOData
  meta: {
    pagination: {
      page: number
      total: number
    }
  }
}

export interface FormValuesPage {
  pagination: Pagination
  createdAt: string
  updatedAt: string
  publishedAt: string
  dataItem: PageData[]
  loading: boolean
  error: string | null
}

export interface CustomBreakpointMap {
  xs?: boolean
  sm?: boolean
  md?: boolean
  lg?: boolean
  xl?: boolean
  xxl?: boolean
}

export interface CustomTokenType {
  padding: number
  paddingXL: number
  sizeXXL: number
  marginLG: number
  fontSizeHeading2: number
  fontSizeHeading3: number
  colorTextSecondary: string
}

export interface ContactFormData {
  nameContact: string
  firstName: string
  lastName: string
  email: string
  numberPhone: string
  slug?: string
  message: string
}
export interface ContactData {
  id: number
  attributes: ContactFormData
}
export interface ContactListProps {
  searchTerm?: string
}
export interface FormValuesContact {
  pagination: Pagination
  createdAt: string
  updatedAt: string
  publishedAt: string
  dataForm: ContactData[]
  loading: boolean
  error: string | null
}
export interface CategoryResponse {
  id: number
  attributes: {
    name: string
    slug: string
    description: string
    createdAt?: string
    updatedAt?: string
    publishedAt?: string
    posts?: PostResponse[]
    products?: Products
  }
}

export interface AttributesCategory {
  name: string
  slug: string
  description: string
  createdAt?: string
  updatedAt?: string
  publishedAt?: string
  posts?: Posts
  products?: ProductResponse[]
}

export interface Posts {
  data: PostResponse[]
}

export interface PostResponse {
  id: number
  attributes: Attributes2
}

export interface Attributes2 {
  name: string
  slug: string
  content: string
  excerpt: string
  createdAt: string
  updatedAt: string
}

export interface FileUpdate {
  fileInfo?: {
    name: string
    alternativeText: string
    caption: string
    width?: number
    height?: number
  }
  file?: File
}
export interface Products {
  data: ProductResponse[]
}

export interface ProductResponse {
  id: number
  attributes: ProductAttributes
}

export interface ProductAttributes {
  name: string
  slug: string
  content: string
  excerpt: string
  price: string
  discount: string
  stock: string
  createdAt: string
  updatedAt: string
  publishedAt: string
}

export interface FormValuesCategory {
  pagination: Pagination
  createdAt: string
  updatedAt: string
  publishedAt: string
  dataItem: CategoryData[]
  loading: boolean
  error: string | null
}

export interface ContactFormProps {
  form: FormInstance
  onFinishFailed: (errorInfo: ValidateErrorEntity) => void
  onFinish: (values: FieldTypeContactForm) => void
}
export type FieldTypeContactForm = {
  name: string
  slug: string
  excerpt: string
  nameContact: string
  firstName: string
  lastName: string
  email: string
  numberPhone: string
  message: string
}

export interface UserUpdatePayload {
  id: number
  username: string
  email: string
  provider: string
  confirmed: boolean
  blocked: boolean
  createdAt: string
  updatedAt: string
  firstName: string
  lastName?: string
  emailSubscription?: boolean
  language?: string
  role: Role
  avatar: {}
}

export interface Role {
  id: number
  name: string
  description: string
  type: string
}

export interface Roles {
  id: number
  name: string
  code: string
  permissions: {
    [module: string]: {
      find?: { enabled: boolean }
      create?: { enabled: boolean }
      update?: { enabled: boolean }
      delete?: { enabled: boolean }
      restore?: { enabled: boolean }
    }
  }
}
export interface FormValuesMedia {
  pagination: Pagination
  createdAt: string
  updatedAt: string
  publishedAt: string
  dataItem: MediaData[]
  loading: boolean
  error: string | null
}

export interface Formats {
  thumbnail: Thumbnail
  large: LargeFile
  medium: MediumFile
  small: SmallFile
}

export interface Thumbnail {
  name: string
  hash: string
  ext: string
  mime: string
  width: number
  height: number
  size: number
  sizeInBytes: number
  url: string
}

export interface LargeFile {
  name: string
  hash: string
  ext: string
  mime: string
  width: number
  height: number
  size: number
  sizeInBytes: number
  url: string
}

export interface MediumFile {
  name: string
  hash: string
  ext: string
  mime: string
  width: number
  height: number
  size: number
  sizeInBytes: number
  url: string
}

export interface SmallFile {
  name: string
  hash: string
  ext: string
  mime: string
  width: number
  height: number
  size: number
  sizeInBytes: number
  url: string
}
export interface DataSettingItem {
  id: number
  attributes: {
    key: string
    value: string
    description: string
  }
}

export interface PayloadSetting {
  key: string
  value: string
  description: string
}

export interface Filters {
  [key: string]: string
}
export interface FilterSearchParams {
  populate?: string
  filters: {
    [key: string]: {
      $containsi?: string
      $null?: boolean
      $eq?: boolean | string
      $gt?: string
      $lt?: string
      $in?: string
      $or?: string
    }
  }
  locale?: string
  sort: string | string[]
  publicationState?: string
  pagination?: {
    page?: string | number
    pageSize?: string | number
    current?: string | number
    start?: string | number
    limit?: string | number
    total?: string | number
    withCount?: boolean
  }
  fields?: string
}
export interface PaginationMeta {
  pagination: {
    total?: number
    page?: number
  }
}

export interface FetchPagesResponse {
  data: any[]
  meta?: PaginationMeta
  error?: {
    message: string
  }
}
// export interface DashboardCardProps {
//   id: number;
//   pageCount: number;
//   postCount: number;
//   userCount: number;
//   commentCount: number;
// }

export interface MenuApiResponse {
  id: number
  attributes: {
    name: string
    slug: string
    order: number
    parent: number | null
  }
}

export interface DataType {
  key: React.Key
  theme: string
  menu: string
}
export interface MenuItemWithSubItems {
  subItem?: MenuItem[]
}
export interface MenuItem {
  id: number
  name: string
  slug: string
  parent: number | null
  order: number
  url: string
}

export interface PageItem {
  name: string
  slug: string
}

export interface PostItem {
  name: string
  slug: string
}

export interface CategoryItem {
  name: string
  slug: string
}

export interface MenuPreference {
  id: number
  name: string
  items: MenuItem[]
}

export interface MenuPreferenceApi {
  id: number
  attributes: {
    name: string
    items: {
      id: number
      name: string
      slug: string
      parent: number
      order: number
    }
    createdAt: string
    updatedAt: string
    publishedAt: string
  }
}

export interface AddCategoryPayload {
  name: string
  slug: string
  description: string
}

export interface BlockContentData {
  id: number
  attributes: AttributesBlockContent
}

export interface AttributesBlockContent {
  content: string
  createdAt: string
  updatedAt: string
  templateName: any
  isTemplate: any
  page: Page
  post: Post
}

export interface Page {
  data: BlockContentPageData
}

export interface Post {
  data: BlockContentPostData
}

export interface BlockContentPageData {
  id: number
  attributes: AttributesBlockContentPageData
}

export interface AttributesBlockContentPageData {
  name: string
  slug: string
  content: string
  createdAt: string
  updatedAt: string
  publishedAt: string
  isUseUIBuilder?: boolean
}

export interface BlockContentPostData {
  id: number
  attributes: AttributesBlockContentPostData
}
export interface AttributesBlockContentPostData {
  name: string
  slug: string
  content: string
  createdAt: string
  updatedAt: string
  publishedAt: string
  isUseUIBuilder?: boolean
}
export interface TemplateData {
  attributes: {
    templateName: string
    isTemplate: boolean
  }
}
export interface GeneralInformationData {
  tags: string[]
  websiteName: string
  websiteDescription: string
}

export interface GeneralInformationDataLP {
  tagsLP: string[]
  websiteNameLP: string
  websiteDescriptionLP: string
}

export interface GeneralInformationLandingPageProps {
  tags: string[]
  landingPageName: string
  landingPageDescription: string
  headerScrips: string
}
export interface FileUploadData {
  structuredDataUrl: string | null
  metaImageUrl: string | null
  faviconUrl: string | null
}

export interface FileUploadDataExtended extends FileUploadData {
  metaImageId?: number | null
  faviconId?: number | null
}

export interface FileUploadDataLP {
  faviconUrlLP: string | null
}
export interface SocialLinksData {
  facebook?: string
  instagram?: string
  linkedin?: string
  youtube?: string
  tiktok?: string
}
export interface EmailData {
  contactEmail: string
  adminEmail: string
}
export interface MarketingSettingData {
  displayTime: string
  hiddenTime: string
  content: string
  image: string[]
}
export interface PopupData {
  displayTimePopup: string
  hiddenTimePopup: string
  templatePopup: string
}

export interface SettingPayload {
  data: {
    key: string
    value: string
  }
}
export interface AttributesRecruitment {
  Title: string
  Content: string
  Department?: string
  Rank?: string
  Quantity?: number
  Form?: boolean
  createdAt?: string
  updatedAt?: string
  publishedAt?: string
}

export interface RecruitmentData {
  id: number
  attributes: AttributesRecruitment
}

export interface FormValuesRecruitment {
  pagination: Pagination
  createdAt: string
  updatedAt: string
  publishedAt: string
  dataItem: RecruitmentData[]
  loading: boolean
  error: string | null
}

export interface AttributesDepartment {
  Name: string
  ShortName?: string
  Description?: string
}

export interface DepartmentData {
  id: number
  attributes: AttributesDepartment
}

export interface DepartmentProps {
  selectedDepartment: string
  onChangeDepartment: (checkedValues: string) => void
}

export interface DepartmentOption {
  id: number
  label?: string | undefined
  value?: number
  disabled?: boolean
}

export interface CandidateFormPayload {
  fullName: string
  phone: string
  email: string
  position: string | number
  description: string
  link: string
}

export interface Category {
  data: {
    id: number
    attributes: {
      name: string
      slug: string
    }
  }
}

export interface AttributesTours {
  is_new: boolean
  is_most_popular?: boolean
  feature_img: (string | { url: string })[]
  title: string
  name?: string
  short_description: string
  description?: string
  price_overview: string
  distance?: string
  duration?: string
  category: Category
  tags: {
    data: TagData[]
  }
  slug: string
}

export interface TourItem {
  id: number
  attributes: AttributesTours
}

export interface DestinationAttributes {
  name: string
  createdAt: string
  updatedAt: string
  publishedAt: string
  tour_v_2s?: {
    data: TourItem[]
  }
}

export interface DestinationItem {
  id: number
  attributes: DestinationAttributes
}

export interface Tours {
  feature_img: string[]
  is_new: boolean
  is_most_popular: boolean
  category: string
  tags: string[]
  title: string
  short_description: string
  price_overview: string
  distance: string
  duration: string
  slug: string
}

export interface JobDetailData {
  id: number
  attributes: JobDetailAttributes
}

export interface JobDetailResponse {
  data: JobDetailData[]
  meta: {
    pagination: {
      page: number
      pageSize: number
      pageCount: number
      total: number
    }
  }
}

export interface JobDetailAttributes {
  JobTitle: string
  Location: string
  Type?: string
  Requirements?: JSON
  Description?: string
  Slug: string
  createdAt?: string
  updatedAt?: string
  publishedAt?: string
}

export interface JobDetailFormProps {
  form: FormInstance
  onFinishFailed: (errorInfo: ValidateErrorEntity) => void
  quillValue?: string
  onFinish: (values: JobDetailAttributes) => void
}

export interface ItemSummary {
  id: number
  label: string
  value: string
}

export interface AvgStats {
  cycling_days: number
  distance_cycling: number
  climbing_days: number
  distance_climbing: number
}

export interface GroupTour {
  id: number
  min_pax: number
  max_pax: number
  price_per_pax: number
  type_group: string
}

export interface OverView {
  label: string
  summary: ItemSummary[]
  avg_stats: AvgStats
  group_tour: GroupTour[]
  private_tour: GroupTour[]
  content_post: string
}

export interface TourFormProps {
  edit?: boolean
  form: FormInstance
  onFinish: (values: any) => void
  dataTour?: object
}

// Customer interfaces
export interface CustomerData {
  id: number
  attributes: {
    ico?: string
    dic?: string
    businessName?: string
    businessRepresentative?: string
    phone: string
    dphRegistered?: boolean
    email: string
    address?: string
    city?: string
    countryOrState?: string
    receiveAtSore?: string
    haveVAT?: boolean
    receiveSMS?: ReceiveSMS
    signature?: string
    idNumber?: string
    occupation?: string
    fullname?: string
    registrationCode?: string
    birthNumber?: string
    isSentEmail?: boolean
    isResolved?: boolean
    createdAt: string
    updatedAt: string
  }
}

export interface CustomerFieldType {
  ico?: string
  dic?: string
  businessName?: string
  businessRepresentative?: string
  phone: string
  dphRegistered?: boolean
  email: string
  address?: string
  city?: string
  countryOrState?: string
  receiveAtSore?: string
  haveVAT?: boolean
  receiveSMS?: ReceiveSMS
  signature?: string
  idNumber?: string
  occupation?: string
  fullname?: string
  registrationCode?: string
  birthNumber?: string
  isSentEmail?: boolean
  isResolved?: boolean
}

export interface CustomerTableProps {
  form: any
  onFinish: FormProps<CustomerFieldType>["onFinish"]
  onFinishFailed: FormProps<CustomerFieldType>["onFinishFailed"]
  onFormChange?: () => void
}

export interface CustomerPayload {
  data: {
    ico?: string
    dic?: string
    businessName?: string
    businessRepresentative?: string
    phone: string
    dphRegistered?: boolean
    email: string
    address?: string
    city?: string
    countryOrState?: string
    receiveAtSore?: string
    haveVAT?: boolean
    receiveSMS?: ReceiveSMS
    signature?: string
    idNumber?: string
    occupation?: string
    fullname?: string
    registrationCode?: string
    birthNumber?: string
    isSentEmail?: boolean
    isResolved?: boolean
  }
}

export type FieldInputType = "input" | "select" | "radio" | "media"

export interface CustomerFieldConfig {
  name: string
  labelKey: string
  type: FieldInputType
  rules?: Rule[] | undefined
  initialValue?: any
  placeholder?: string
  options?: Array<{ label: string; value: any }>
  hideOnEdit?: boolean
  column?: "left" | "right"
}

export interface SelectOption {
  value: string | number
  label: string
}

export interface BannerOptions {
  pageOptions: SelectOption[]
  positionOptions: SelectOption[]
  websiteOptions: SelectOption[]
  targetOptions: SelectOption[]
}

export interface WebsiteData {
  id: number
  attributes: {
    name: string
    domain: string
  }
}

export interface ApiResponse<T> {
  data: T[]
}

export interface BannerData {
  id: number
  attributes: {
    name: string
    description: string | null
    position: string
    page: string
    url: string
    order: number
    target: string
    locale: string
    publishedAt?: string
    isActive?: boolean
    banner?: {
      data?: {
        id: number
        attributes: {
          url: string
        }
      }
    }
    mobileBanner?: {
      data?: {
        id: number
        attributes: {
          url: string
        }
      }
    }
    website?: {
      data?: {
        id: number
        attributes: {
          name: string
        }
      }
    }
    common?: {
      id?: number
      clickUrl?: string
      impressionHtml?: any[]
      vendorScriptSrc?: string
      vendorNoscriptHtml?: string
    }
  }
}

export interface Permissions {
  banner?: {
    create?: { enabled: boolean }
    update?: { enabled: boolean }
    delete?: { enabled: boolean }
  }
  user?: {
    create?: { enabled: boolean }
    update?: { enabled: boolean }
    findOne?: { enabled: boolean }
    destroy?: { enabled: boolean }
    me?: { enabled: boolean }
    find?: { enabled: boolean }
  }
}

export interface StrapiPermissionItem {
  enabled: boolean
  policy?: string
}

export type StrapiPermissionActions = "find" | "findOne" | "create" | "update" | "delete" | "publish"

export type StrapiPermissions = {
  [resource: string]: {
    [action in StrapiPermissionActions]?: StrapiPermissionItem
  }
}

export type CustomerFormData = CustomerPayload["data"]
export interface LetakyData {
  id: number
  attributes: LetakyAttributes
}

export interface LetakyAttributes {
  name: string
  title: string
  slug: string
  content: string
  dateStart: string
  dateEnd: string
  locale: string
  publishedAt: string | null
  scheduledAt?: string | null
  unpublishAt?: string | null
  createdAt: string
  updatedAt: string
  localizations: {
    data: LetakyData[]
  }
}

export interface CreateLetakyData {
  name: string
  title: string
  slug: string
  content: string
  dateStart: string
  dateEnd: string
  locale: string
}

export interface UpdateLetakyData extends Partial<CreateLetakyData> {
  // All fields are optional for updates
}

// Header related interfaces
export interface HeaderBannerData {
  id?: number
  imageUrl: string
  altText?: string
  link?: string
  target?: string
  trackingConfig?: {
    clickUrl?: string
    impressionHtml?: any[]
    vendorScriptSrc?: string
    vendorNoscriptHtml?: string
  }
}

export interface BannerProps {
  data?: HeaderBannerData
  className?: string
}

export interface HeaderTemplateData {
  id: number
  attributes: {
    content: string
    templateName?: string
    createdAt?: string
    updatedAt?: string
    locale?: string
    [key: string]: any
  }
}

export interface CurrencyRateData {
  id: number
  attributes: {
    key: string
    value: string
    description: string
  }
}

export interface HeaderContactInfo {
  phone?: string
  email?: string
  hotline?: string
}

export interface HeaderSocialLinks {
  facebook?: string
  youtube?: string
  facebookIcon?: string
  youtubeIcon?: string
}

export interface HeaderDetailData {
  currentDate: string
  currencyRate: string
  contactInfo: HeaderContactInfo
  socialLinks?: HeaderSocialLinks
  headerLogo?: string
}

export interface TamdaMediaSettings {
  phone?: string
  email?: string
  advertisingEmail?: string
  facebook?: string
  youtube?: string
  tagline?: string
}

export interface TamdaMediaSettingData {
  id: number
  attributes: {
    phoneNumber?: string
    tagline?: string
    facebook?: string
    twitter?: string
    linkedIn?: string
    hotline?: string
    email?: string
    advertisingEmail?: string
    youtube?: string
    [key: string]: string | undefined
  }
}

export interface TamdaMediaSettingResponse {
  data?: TamdaMediaSettingData
  meta?: Meta
}

export interface BannerRenderConfigProps {
  data?: HeaderBannerData
  [key: string]: any
}

export interface HeaderDetailProps {
  data: HeaderDetailData
  className?: string
  onSearchClick?: () => void
}

export interface HeaderDetailRenderConfigProps {
  data: HeaderDetailData
  onSearchClick?: () => void
  [key: string]: any
}

// Bottom navigation props for mobile header
export interface BottomNavProps {
  locale: string
  onMenuClick: () => void
  socialLinks?: HeaderSocialLinks
}

export interface HeaderListProps {
  menuData: HeaderMenuData | null
  locale: string
  shouldBeTransparent?: boolean
  isScrolled?: boolean
  className?: string
  onSearchClick?: () => void
  socialLinks?: {
    facebook?: string
    youtube?: string
    facebookIcon?: string
    youtubeIcon?: string
  }
}

export interface HeaderListRenderConfigProps {
  menuData: HeaderMenuData | null
  locale: string
  shouldBeTransparent?: boolean
  isScrolled?: boolean
  onSearchClick?: () => void
  socialLinks?: {
    facebook?: string
    youtube?: string
    facebookIcon?: string
    youtubeIcon?: string
  }
  blogImage?: string
  mediaImage?: string
  tourImage?: string
  [key: string]: any
}

export interface HeaderMenuItemAttribute {
  id: number
  name: string
  slug: string
  url: string
  order: number
  chosen: boolean
  parent: number
  selected: boolean
}

export interface HeaderMenuData {
  id: number
  attributes: {
    name: string
    items: HeaderMenuItemAttribute[]
  }
}

export interface HeaderMenuPreferenceResponse {
  data: HeaderMenuData
}

export interface HeaderSettingsResponse {
  data: DataSettingItem[]
}

export interface HeaderSettings {
  banner: HeaderBannerData
  menuData: HeaderMenuData | null
  currencyRate: CurrencyRateData | null
  currentDate: string
}

export interface UseHomepageReturn {
  headerSettings: HeaderSettings | null
  loading: boolean
  error: string | null
}

export type FetchPostByCategoryParams = {
  sort?: string
  fields?: string
  populate?: string
  locale?: string
  pagination?: {
    withCount?: boolean
    page?: number
    pageSize?: number
    start?: number
    limit?: number
  }
  filters?: Record<string, unknown>
}

export interface AllPostsProps {
  posts: PostData[]
  currentPage: number
  totalPages: number
  categorySlug: string
  locale?: string
  isVideo?: boolean
  youtubeUrlMap?: Record<number, string>
  onVideoClick?: (youtubeUrl: string) => void
  onPageChange?: (page: number) => void
}

export interface BreakingPostsProps {
  posts: PostData[]
  locale?: string
}

export interface FeaturedPostClassNameProps {
  className?: string
  contentClassName?: string
  imageClassName?: string
  titleClassName?: string
  descriptionClassName?: string
  dateClassName?: string
  isVideo?: boolean
  youtubeUrl?: string
  onVideoClick?: (youtubeUrl: string) => void
}

export interface PostByCategoryPageProps {
  posts: PostData[]
  meta: Meta
  breakingPosts?: PostData[]
  hasBreadcrumb?: boolean
  categoryName?: string
  categorySlug?: string
  locale?: string
  banner?: BannerData | null
  currentPage?: number
  isVideoPage?: boolean
  youtubeUrlMap?: Record<number, string>
  // Cụm tamdaone (siteId !== "tamdamedia.eu") render hero banner breadcrumb thay
  // cho promo banner; heroBannerUrl là ảnh banner riêng của category (đã fallback).
  siteId?: SiteId
  heroBannerUrl?: string | null
}

export interface SidePostsProps {
  posts: PostData[]
  locale?: string
  isVideo?: boolean
  youtubeUrlMap?: Record<number, string>
  onVideoClick?: (youtubeUrl: string) => void
}

export interface SubFeaturePostsProps {
  posts: PostSubData[]
  locale?: string
  isVideo?: boolean
  youtubeUrlMap?: Record<number, string>
  onVideoClick?: (youtubeUrl: string) => void
}

// Type conversion interface for PostData from lib/config/types to types/types
// This allows safe type casting between the two PostData definitions
// Using Partial<SeoData> to allow compatibility with both SeoData definitions
export interface PostDataFromService {
  id: number
  attributes: {
    name?: string
    excerpt?: string
    title: string
    slug: string
    content: string
    description: string
    readingTime?: number
    shortDescription?: string
    createdAt: string
    updatedAt: string
    publishedAt: string
    locale: string
    views: number
    old_id: number | null
    isLiveHappening: boolean | null
    isLiveNews: boolean | null
    liveNewsItem: LiveNewsItemData[]
    isUseUIBuilder?: boolean
    author?: {
      data?: {
        id: number
        attributes: UserAttributes
      }
    }
    blockContent: {
      data?: BlockContent | null
    }
    image: {
      data?: DataImage | null
    }
    cover?: {
      data?: DataImage | null
    }
    categories: {
      data: CategoryData[]
    }
    tags: {
      data: TagData[]
    }
    seo: {
      id?: number
      metaTitle?: string
      metaDescription?: string
      keywords?: string
      metaKeywords?: string
      metaRobots?: string
      metaViewport?: string
      structuredData?: JSON
      canonicalURL?: string
    }
  }
}

export interface UseCategoryOptions {
  searchTerm?: string
  refreshKey?: number
  autoFetch?: boolean
  initialPageSize?: number
  /** Default sort attribute from "Configure view" (e.g. "name"). */
  sortField?: string
  sortOrder?: "asc" | "desc"
  /** Populate the visible list columns need (Configure-the-view). */
  listPopulate?: Record<string, unknown>
  filters?: {
    name?: string
    type?: "post" | "video" | ""
    domain?: string
    locale?: string
    state?: "published" | "draft" | ""
  }
}

export interface UseCategoryReturn {
  // Data
  categories: CategoryData[]
  category: CategoryData | null
  loading: boolean
  error: string | null
  pagination: Pagination

  // Actions
  fetchCategories: (searchParams?: { [key: string]: string }, publicationState?: string) => Promise<CategoryData[]>
  fetchCategoryById: (id: number) => Promise<CategoryData | null>
  createCategory: (data: AttributesCategory) => Promise<boolean>
  updateCategory: (id: string, data: AttributesCategory) => Promise<boolean>
  deleteCategory: (id: number) => Promise<boolean>

  // Duplicate & Translate
  duplicateCategory: (id: number) => Promise<boolean>
  resolveLocalizationTarget: (record: CategoryData, locale: string) => Promise<number | undefined>

  // Table handlers
  handleTableChange: (pagination: TablePaginationConfig, filters?: unknown, sorter?: unknown) => void
  handleDelete: (id: number) => void

  // Permissions
  permissions: Record<string, any> | null
  hasPermission: (action: "create" | "update" | "delete") => boolean
}

export interface HeroBannerButton {
  buttonName: string
  buttonType: "primary" | "outline"
  buttonLink: string
  buttonBg?: string
  // Button styles for individual button
  buttonSize?: string // e.g., "width: 200px; height: 50px"
  buttonFontSize?: string
  buttonFontWeight?: string
  buttonLineHeight?: string
  // Button hover styles
  hoverBackgroundColor?: string
  hoverColor?: string
  hoverBorder?: string
  hoverTransform?: string // e.g., "scale(1.1)"
  hoverOpacity?: string
  hoverBoxShadow?: string
}

export interface HeroBannerBackground {
  image: string
  height?: string
}

export interface HeroBannerButtonStyles {
  size?: string // e.g., "width: 200px; height: 50px"
  fontSize?: string
  fontWeight?: string
  lineHeight?: string
  gap?: string
}

export interface HeroBannerSlide {
  background?: HeroBannerBackground
  backgroundImage?: string // Keep for backward compatibility
  topText?: string
  mainText?: string
  mainTextStyles?: {
    fontSize?: string
    fontWeight?: string
    lineHeight?: string
  }
  listButtons?: HeroBannerButton[]
}

export interface HeroBannerSwiperConfig {
  enabled?: boolean
  autoplay?: boolean
  autoplayDelay?: number
  loop?: boolean
  navigation?: boolean
  pagination?: boolean
  spaceBetween?: number
  speed?: number
}

export interface HeroBannerLayoutProps {
  // Swiper mode - multiple slides
  slides?: HeroBannerSlide[]
  swiperConfig?: HeroBannerSwiperConfig
  // Single slide mode (backward compatibility)
  background?: HeroBannerBackground
  backgroundImage?: string // Keep for backward compatibility
  topText?: string
  mainText?: string
  mainTextStyles?: {
    fontSize?: string
    fontWeight?: string
    lineHeight?: string
  }
  listButtons?: HeroBannerButton[]
  buttonStyles?: HeroBannerButtonStyles
  className?: string
  styles?: {
    mobile?: Record<string, any>
    tablet?: Record<string, any>
    desktop?: Record<string, any>
  }
  responsiveType?: "desktopFirst" | "mobileFirst"
  // Legacy props for backward compatibility
  mainTextFontSize?: string
  mainTextFontWeight?: string
  mainTextLineHeight?: string
  backgroundHeight?: string
  buttonSize?: string
  buttonFontSize?: string
  buttonFontWeight?: string
  buttonLineHeight?: string
  buttonGap?: string
}

export interface BreadcrumbItem {
  title: string
  link: string
}

export interface HeroBannerWithBreadcrumbProps {
  breadcrumb: BreadcrumbItem[]
  image: string
  title: string
  className?: string
  styles?: {
    mobile?: Record<string, any>
    tablet?: Record<string, any>
    desktop?: Record<string, any>
  }
  responsiveType?: "desktopFirst" | "mobileFirst"
}

export interface HeroBannerWithBreadcrumbContentProps {
  id: string
  breadcrumb: BreadcrumbItem[]
  image: string
  title: string
  className?: string
  isTamdaOne?: boolean
}

// QuickLinks interfaces
export interface QuickLinkItem {
  title: string
  description?: string
  image?: string
  link: string
}

export interface QuickLinksProps {
  links: QuickLinkItem[]
  className?: string
  styles?: {
    mobile?: Record<string, any>
    tablet?: Record<string, any>
    desktop?: Record<string, any>
  }
  responsiveType?: "desktopFirst" | "mobileFirst"
}

export interface QuickLinksContentProps {
  id: string
  links: QuickLinkItem[]
  className?: string
  locale?: string
}

export interface QuickServiceItem {
  image: string
  text: string
  textHover: string
  icon?: string
  title?: string
  description?: string
  arrowIcon?: string
}

export interface QuickServiceProps extends CommonStylesProps {
  title?: string
  items: QuickServiceItem[]
}

export interface QuickServiceContentProps {
  id: string
  title?: string
  items?: QuickServiceItem[]
  className?: string
}

export interface QuickServiceItemProps {
  item: QuickServiceItem
  className?: string
  isLarge?: boolean
  enableHover?: boolean
  index?: number
}

export interface UseQuickServiceProps {
  items?: QuickServiceItem[]
  componentId?: string
}

export interface UseQuickServiceResult {
  id: string
  validItems: QuickServiceItem[]
  hasValidItems: boolean
}

export interface ServiceGridListItem {
  image: string
  text: string
  textHover: string
  icon?: string
  title?: string
  description?: string
  arrowIcon?: string
  link?: string
}

export interface ServiceGridListProps extends CommonStylesProps {
  title?: string
  items: ServiceGridListItem[]
}

export interface ServiceGridListContentProps {
  id: string
  title?: string
  items?: ServiceGridListItem[]
  className?: string
}

export interface ServiceGridListItemProps {
  item: ServiceGridListItem
  className?: string
  isLarge?: boolean
  enableHover?: boolean
  index?: number
}

// About Us Timeline interfaces
export interface TimelineItem {
  year: string
  image?: string
  title: string
  date: string
  subtitle: string
  description: string
}

export interface AboutUsTimelineStyles {
  id?: string
  display?: string
  margin?: string
  padding?: string
  width?: string
  height?: string
  minWidth?: string
  minHeight?: string
  maxWidth?: string
  maxHeight?: string
  backgroundColor?: string
  backgroundImage?: string
  color?: string
  fontSize?: string
  fontWeight?: string
  lineHeight?: string
  textAlign?: string
  alignItems?: string
  alignContent?: string
  justifyContent?: string
  justifyItems?: string
  fontFamily?: string
  borderRadius?: string
  border?: string
  gap?: string
  flexDirection?: string
  customCSS?: React.CSSProperties
  top?: string
  left?: string
  right?: string
  zIndex?: string
  position?: string
}

export interface AboutUsTimelineProps {
  title: string
  items: TimelineItem[]
  logo?: string
  autoplay?: boolean
  autoplaySpeed?: number
  speed?: number
  arrows?: boolean
  dots?: boolean
  infinite?: boolean
  fade?: boolean
  className?: string
  styles?: {
    mobile?: AboutUsTimelineStyles
    tablet?: AboutUsTimelineStyles
    desktop?: AboutUsTimelineStyles
  }
  responsiveType?: "desktopFirst" | "mobileFirst"
  animation?: any
}

export interface AnimatedButtonProps {
  button: HeroBannerButton
  index: number
  totalButtons: number
  buttonStyle?: any // Object chứa tất cả các style props để linh động
  // Giữ lại các props cũ để backward compatibility
  buttonSize?: { width?: string; height?: string }
  buttonFontSize?: string
  buttonFontWeight?: string
  buttonLineHeight?: string
  hoverBackgroundColor?: string
  hoverColor?: string
  hoverBorder?: string
  hoverTransform?: string
  hoverOpacity?: string
  hoverBoxShadow?: string
}

export interface OutlineToPrimaryButtonProps {
  buttonName: string
  buttonLink: string
  buttonBg?: string
  index: number
  buttonStyle?: any // Object chứa tất cả các style props để linh động
  // Giữ lại các props cũ để backward compatibility
  buttonSize?: { width?: string; height?: string }
  buttonFontSize?: string
  buttonFontWeight?: string
  buttonLineHeight?: string
  hoverBackgroundColor?: string
  hoverColor?: string
  hoverBorder?: string
  hoverTransform?: string
  hoverOpacity?: string
  hoverBoxShadow?: string
}

export interface HeroBannerContentProps {
  id: string
  // Swiper mode - multiple slides
  slides?: HeroBannerSlide[]
  swiperConfig?: HeroBannerSwiperConfig
  // Single slide mode (backward compatibility)
  background?: HeroBannerBackground
  backgroundImage?: string // Keep for backward compatibility
  topText?: string
  mainText?: string
  mainTextStyles?: {
    fontSize?: string
    fontWeight?: string
    lineHeight?: string
  }
  listButtons?: HeroBannerButton[]
  buttonStyles?: HeroBannerButtonStyles
  className?: string
  // Legacy props for backward compatibility
  mainTextFontSize?: string
  mainTextFontWeight?: string
  mainTextLineHeight?: string
  backgroundHeight?: string
  buttonSize?: string
  buttonFontSize?: string
  buttonFontWeight?: string
  buttonLineHeight?: string
  buttonGap?: string
}
export interface TimelineNavigationProps {
  years: string[]
  activeIndex: number
  onYearClick: (index: number) => void
  onPrevClick: () => void
  onNextClick: () => void
  canGoPrev: boolean
  canGoNext: boolean
}

export interface MemberItem {
  image: string
  name: string
  position: string
  link?: string
}

export interface MemberListProps {
  title: string
  description: string
  members: MemberItem[]
  className?: string
  styles?: {
    mobile?: any
    tablet?: any
    desktop?: any
  }
  responsiveType?: "desktopFirst" | "mobileFirst"
  animation?: any
}

export interface MemberListItemProps {
  member: MemberItem
  className?: string
}

export interface MemberListGridProps {
  members: MemberItem[]
  className?: string
}

export interface MemberListTitleProps {
  title: string
  description: string
}

export interface AboutUsSectionProps {
  title: string
  description: string
  floorPlanImage: string
  logo: string
  mainImage: string
  className?: string
  styles?: {
    mobile?: any
    tablet?: any
    desktop?: any
  }
  responsiveType?: "desktopFirst" | "mobileFirst"
  animation?: any
}

export interface TopSectionItem {
  number: string
  text: string
}

export interface CompanyValue {
  title: string
  description: string
}

export interface TopDescription {
  text: string
}

export interface TopSectionProps {
  image?: string
  items?: TopSectionItem[]
  descriptions?: TopDescription[]
  className?: string
}

export interface VisionAndMissionProps {
  topImage?: string
  topItems?: TopSectionItem[]
  topDescriptions?: TopDescription[]
  bottomImage?: string
  visionTitle?: string
  visionDescription?: string
  missionTitle?: string
  missionDescription?: string
  companyValues?: CompanyValue[]
  className?: string
  styles?: {
    mobile?: any
    tablet?: any
    desktop?: any
  }
  responsiveType?: "desktopFirst" | "mobileFirst"
  animation?: any
}

// Banner Carousel types
export interface BannerItem {
  image: string
  url?: string
  target?: string
  trackingConfig?: {
    clickUrl?: string
    impressionHtml?: any[]
    vendorScriptSrc?: string
    vendorNoscriptHtml?: string
  }
}

export interface BannerCarouselProps extends CommonStylesProps {
  banners: BannerItem[]
}

export interface UseBannerCarouselProps {
  banners?: BannerItem[]
  componentId?: string
  autoplayDelay?: number
}

export interface UseBannerCarouselResult {
  validBanners: BannerItem[]
  hasValidBanners: boolean
  swiperConfig: {
    loop: boolean
    autoplay: boolean | { delay: number; disableOnInteraction: boolean }
    pagination: { clickable: boolean }
  }
  id: string
}

export interface BannerCarouselContentProps {
  id: string
  banners?: BannerItem[]
  className?: string
}

// Promotion Bottom Banner types
export interface PromotionBottomBannerItem {
  image: string
  title: string
  buttonText: string
  buttonLink: string
  bannerUrl?: string
  bannerTarget?: string
  trackingConfig?: {
    clickUrl?: string
    impressionHtml?: any[]
    vendorScriptSrc?: string
    vendorNoscriptHtml?: string
  }
}

export interface PromotionBottomBannerProps extends CommonStylesProps {
  banners: PromotionBottomBannerItem[]
  mobileHeight?: string
  desktopHeight?: string
}

export interface UsePromotionBottomBannerProps {
  banners?: PromotionBottomBannerItem[]
  componentId?: string
  autoplayDelay?: number
}

export interface UsePromotionBottomBannerResult {
  validBanners: PromotionBottomBannerItem[]
  hasValidBanners: boolean
  swiperConfig: {
    loop: boolean
    autoplay: boolean | { delay: number; disableOnInteraction: boolean }
    pagination: { clickable: boolean }
  }
  id: string
}

export interface PromotionBottomBannerContentProps {
  id: string
  banners?: PromotionBottomBannerItem[]
  mobileHeight?: string
  desktopHeight?: string
  className?: string
}

// Company List types
export interface CompanyItem {
  image: string
  companyName?: string
  width?: number
  height?: number
  paddingTop?: number
  paddingRight?: number
  paddingBottom?: number
  paddingLeft?: number
}

export interface CompanyListProps extends CommonStylesProps {
  title?: string
  gap?: number
  companies: CompanyItem[]
  maxWidth?: number
  titleFontSize?: number
  titleFontWeight?: number
  titleLineHeight?: number
}

export interface UseCompanyListProps {
  companies?: CompanyItem[]
  componentId?: string
}

export interface UseCompanyListResult {
  validCompanies: CompanyItem[]
  hasValidCompanies: boolean
  swiperConfig: {
    loop: boolean
    pagination: { clickable: boolean }
  }
  id: string
}

export interface CompanyListContentProps {
  id: string
  title?: string
  companies?: CompanyItem[]
  className?: string
  gap?: number
  maxWidth?: number
  titleFontSize?: number
  titleFontWeight?: number
  titleLineHeight?: number
}

export interface CompanyListItemProps {
  company: CompanyItem
  className?: string
  style?: React.CSSProperties
}

export interface WhyChooseUsItem {
  image: string
  title: string
}

export interface WhyChooseUsProps extends CommonStylesProps {
  title?: string
  items: WhyChooseUsItem[]
}

export interface UseWhyChooseUsProps {
  items?: WhyChooseUsItem[]
  componentId?: string
}

export interface UseWhyChooseUsResult {
  validItems: WhyChooseUsItem[]
  hasValidItems: boolean
  swiperConfig: {
    loop: boolean
    pagination: { clickable: boolean }
  }
  id: string
}

export interface WhyChooseUsContentProps {
  id: string
  title?: string
  items?: WhyChooseUsItem[]
  className?: string
}

export interface WhyChooseUsItemProps {
  item: WhyChooseUsItem
  className?: string
  isActive?: boolean
  onClick?: () => void
}

export interface NotificationSelectValue {
  postId: number
  postTitle: string
  postSlug?: string
}

export interface NotificationSelectProps {
  value?: NotificationSelectValue[]
  onChange: (value: NotificationSelectValue[]) => void
  categoryId?: number | null
  label?: string
  placeholder?: string
  pageSize?: number
}

export interface UseNotificationListResult {
  posts: PostData[]
  loading: boolean
  error: string | null
  id: string
  postIds: number[]
  finalViewAllLink?: string
  locale: string
}

export interface NotificationListContentProps {
  id: string
  title?: string
  viewAllLink?: string
  posts: PostData[]
  loading: boolean
  className?: string
  locale?: string
}

export interface NotificationListSkeletonProps {
  className?: string
}

export interface UseNotificationSelectResult {
  allPosts: PostData[]
  currentPage: number
  hasMore: boolean
  loading: boolean
  options: Array<{ value: number; label: string }>
  selectedPostIds: number[]
  loadPosts: (page: number, reset?: boolean) => Promise<void>
  handleLoadMore: () => void
  handleChange: (selectedValues: number[]) => void
}

export interface NotificationListRenderProps {
  title?: string
  viewAllLink?: string
  category?: {
    categoryId: number
    categoryName: string
    slug?: string
  }
  locale?: string
  className?: string
  styles?: {
    mobile?: Record<string, unknown>
    tablet?: Record<string, unknown>
    desktop?: Record<string, unknown>
  }
  responsiveType?: "desktopFirst" | "mobileFirst"
  id?: string
}
// Category Express interfaces
export interface CategoryExpressItem {
  image: string
  name: string
  link: string
  isViewAll?: boolean
}

export interface CategoryExpressProps extends CommonStylesProps {
  title?: string
  items: CategoryExpressItem[]
}

export interface UseCategoryExpressProps {
  items?: CategoryExpressItem[]
  componentId?: string
}

export interface UseCategoryExpressResult {
  validItems: CategoryExpressItem[]
  hasValidItems: boolean
  id: string
  itemRefs: { current: (HTMLDivElement | null)[] }
  activeItemRef: { current: number | null }
}

export interface CategoryExpressContentProps {
  id: string
  title?: string
  items?: CategoryExpressItem[]
  className?: string
}

export interface CategoryExpressItemProps {
  item: CategoryExpressItem
  className?: string
}

// Widget Registration interfaces
export interface WidgetRegistrationItem {
  image: string
  title: string
  description: string
  hasEmailInput?: boolean
  buttonType: "link" | "onClick"
  buttonText: string
  buttonLink?: string
  buttonOnClick?: string
}

export interface WidgetRegistrationProps extends CommonStylesProps {
  items: WidgetRegistrationItem[]
  title?: string
}

export interface UseWidgetRegistrationProps {
  items?: WidgetRegistrationItem[]
  componentId?: string
}

export interface UseWidgetRegistrationResult {
  validItems: WidgetRegistrationItem[]
  hasValidItems: boolean
  id: string
  siteId: string | null
}

export interface WidgetRegistrationContentProps {
  id: string
  items?: WidgetRegistrationItem[]
  className?: string
  title?: string
  isCMS?: boolean
}

export interface WidgetRegistrationItemProps {
  item: WidgetRegistrationItem
  className?: string
  isCMS?: boolean
}

// PromotionList interfaces
export interface PostSelectValue {
  postId: number
  postTitle: string
  postSlug?: string
}

export interface PostSelectProps {
  value?: number
  onChange?: (value: number) => void
  categoryId?: number | null
  label?: string
  placeholder?: string
  pageSize?: number
}

export interface PromotionListContentProps {
  id: string
  title?: string
  viewAllLink?: string
  locale?: string
  posts: PostData[]
  loading: boolean
  slidesPerView?: number
  spaceBetween?: number
  className?: string
}

export interface PromotionListRenderProps {
  title?: string
  viewAllLink?: string
  locale?: string
  category?: {
    categoryId: number
    categoryName: string
    slug?: string
  }
  pageSize?: number
  slidesPerView?: number
  spaceBetween?: number
  className?: string
  styles?: {
    mobile?: Record<string, unknown>
    tablet?: Record<string, unknown>
    desktop?: Record<string, unknown>
  }
  responsiveType?: "desktopFirst" | "mobileFirst"
  id?: string
}

export interface PromotionListSkeletonProps {
  slidesPerView?: number
  spaceBetween?: number
  className?: string
}

export interface UsePromotionListResult {
  posts: PostData[]
  loading: boolean
  error: string | null
}

export interface CategoryStore {
  categoryMap: Map<string, number | null>
  setCategoryId: (instanceId: string, id: number | null) => void
  getCategoryId: (instanceId: string) => number | null
}

export interface ServiceSidebarCategory {
  categoryName: string
  link?: string
  contentType?: "ckeditor" | "career" | "faqs" | "link"

  // Dành cho cách cũ (nếu còn dùng)
  contentCkeditor?: string
  faqs?: { question: string; answer: string }[]

  // Dành cho Puck UI Builder – đây mới là chuẩn hiện tại
  content?: {
    type: string // "FAQs" | "RichText" | "CKEditor" | ...
    props: {
      faqs?: { question: string; answer: string }[]
      content?: string
      value?: string
      [key: string]: any
    }
  }

  // FAQs Theme Selection
  faqsThemeId?: string // ID of selected theme
  faqsContent?: { question: string; answer: string }[] // Parsed FAQs data from theme

  // FAQs Page Selection
  faqsPageId?: string | number // ID of selected page
  faqsPageContent?: { question: string; answer: string }[] // Parsed FAQs data from page
}

export interface ServiceSidebarContentProps {
  id: string
  className?: string
  titleCategory: string
  category: ServiceSidebarCategory[]
  responsiveCSS: React.ReactNode
  puck?: any
}

export interface ServiceDetailProps {
  data: any
  locale: string
  phoneNumber?: string
}

export interface ServiceFormApplyProps {
  jobDetail?: any
  locale: string
  phoneNumber?: string
}

export interface CareerJobDetailItem {
  icon: string
  labelKey: string
  valueKey: string
  alt: string
}

export interface ServiceSidebarProps extends CommonStylesProps {
  titleCategory: string
  category: ServiceSidebarCategory[]
}

export interface UseServiceFormApplyOptions {
  jobDetail?: any
  locale: string
  form: FormInstance
}

export interface UseServiceFormApplyReturn {
  showAlert: boolean
  isMessageSent: boolean
  loading: boolean
  selectedFile: File | undefined
  fileInputRef: React.RefObject<HTMLInputElement | null>
  handleUploadClick: (e: React.MouseEvent<HTMLButtonElement>) => void
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onFinish: (values: any) => Promise<void>
}

export interface UseServiceSidebarOptions {
  activeContentType?: "ckeditor" | "career" | "faqs" | "link"
  locale?: string
}

export interface UseServiceSidebarReturn {
  careers: any[]
  loading: boolean
  error: string | null
  expandedMap: Record<string, boolean>
  setExpandedMap: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
  fetchCareers: (locale?: string) => void
  setLocale: (locale: string) => void
}

export interface ServiceSidebarNavItemProps {
  categoryName: string
  link?: string | null
  isActive: boolean
  onClick: () => void
  contentType?: "ckeditor" | "career" | "faqs" | "link"
}

// FAQs interfaces
export interface FAQsItem {
  question: string
  answer: string
}

export interface FAQsProps extends CommonStylesProps {
  titleCategory?: string
  faqs: FAQsItem[]
}

export interface FAQsContentProps extends Omit<FAQsProps, "styles" | "responsiveType"> {
  id: string
  responsiveCSS: React.ReactNode
}

export interface SidebarPost {
  id: string
  title: string
  slug: string
  summary: string
  imageUrl?: string
  publishedAt: string
  categoryId: string
}

export interface SocialShareSettings {
  facebook?: string
  twitter?: string
}

export interface Product {
  id: string
  name: string
  ean: string
  companyId: string
  originPrice: string
  nonBundlePrice: string
  bundlePrice: string
  bundleSize: number
  qf: boolean
  expiryDate: string | null
  inStockCount: number
  isLetak: boolean
  discountPercentage: string
  isSpecial: boolean
  isNew: boolean
  eanMask: string
  image: {
    origin: string
    md: string
    sm: string
    lg: string
    xsm: string
  }
  slug: string
  url: string
}

export interface ApiProduct {
  product_id: string
  product_code: string
  product_type: string
  status: string
  company_id: string
  list_price: string
  unit_per_box: string
  unit_per_box_name: string
  amount: string
  weight: string
  timestamp: string
  updated_timestamp: string
  is_returnable: string
  return_period: string
  min_qty: string
  max_qty: string | null
  qty_step: string
  tax_ids: string
  product: string
  name_without_symbols: string
  short_description: string | null
  full_description: string | null
  search_words: string
  barcode_1: string
  barcode_2: string
  barcode_3: string
  reversed_barcode: string
  main_pair: {
    pair_id: string
    image_id: string
    detailed_id: string
    detailed: {
      image_path: string
      alt: string
      image_x: string
      image_y: string
      http_image_path: string
      https_image_path: string
      absolute_path: string
      relative_path: string
    }
  }
  base_price: string
  price: string
  url: string
  product_features: {
    [key: string]: {
      feature_id: string
      value: string
      feature_code: string
      description: string
    }
  }
  qty_content: number[]
  new_label_to_date: string
  new_label_from_date: string
  letak_to_date: string
  letak_from_date: string
}

export interface TamdaExpressProductListProps {
  eanIds?: string
  title?: string
  className?: string
}

export interface GalleryPhoto {
  id?: number
  attributes?: {
    name?: string
    alternativeText?: string
    caption?: string
    width?: number
    height?: number
    formats?: any
    hash?: string
    ext?: string
    mime?: string
    /** @format float */
    size?: number
    url?: string
    previewUrl?: string
    provider?: string
    provider_metadata?: any
    related?: {
      data?: {
        id?: number
        attributes?: object
      }[]
    }
    folder?: {
      data?: {
        id?: number
        attributes?: object
      }
    }
    folderPath?: string
    /** @format date-time */
    createdAt?: string
    /** @format date-time */
    updatedAt?: string
    createdBy?: {
      data?: {
        id?: number
        attributes?: object
      }
    }
    updatedBy?: {
      data?: {
        id?: number
        attributes?: object
      }
    }
  }
}

export interface FooterPageItem {
  title: string
  url: string
}

export interface FooterLink {
  title: string
  url: string
}

export interface FooterDataResult {
  menuData: HeaderMenuData | null
}

export interface FooterMediaProps {
  locale?: string
  className?: string
  logoImage?: string
  slogan?: string
  hotCategory?: FooterPageItem[]
  regularCategory1?: FooterPageItem[]
  regularCategory2?: FooterPageItem[]
  regularCategory3?: FooterPageItem[]
  phone?: string
  email?: string
  advertisingContact?: string
  facebookIcon?: string
  facebookUrl?: string
  youtubeIcon?: string
  youtubeUrl?: string
  copyrightText?: string
}

// Settings fetch sẵn ở server rồi truyền xuống footer client. Khi có prop này,
// FooterGroup KHÔNG fetch lại ở client (bỏ zustand + useEffect). Optional để
// vẫn dùng được trong ui-builder preview (không có server settings → fallback).
export interface FooterGroupSettings {
  facebookUrl?: string
  twitterUrl?: string
  youtubeUrl?: string
  copyright?: string
  footerLogoUrl?: string
}

export interface FooterGroupProps {
  locale: string
  className?: string
  aboutLinks?: FooterLink[]
  infoLinks?: FooterLink[]
  office?: string
  about?: string
  info?: string
  copyrightFallback?: string
  settings?: FooterGroupSettings
}

export interface TamdaMediaFooterViewClientProps {
  locale: string
  siteId: SiteId
  hotCategory: FooterPageItem[]
  regularCategory1: FooterPageItem[]
  regularCategory2: FooterPageItem[]
  regularCategory3: FooterPageItem[]
  phone?: string
  email?: string
  advertisingContact?: string
  facebookUrl?: string
  youtubeUrl?: string
  slogan?: string
  copyrightText?: string
  logoImage?: string
  facebookIcon?: string
  youtubeIcon?: string
  className?: string
}

export interface TamdaMediaFooterServerProps {
  locale: string
  siteId: SiteId
}

export interface TamdaOneFooterViewClientProps {
  locale: string
  aboutLinks: FooterPageItem[]
  infoLinks: FooterPageItem[]
  copyright?: string
  office?: string
  about?: string
  info?: string
  settings?: FooterGroupSettings
}

export interface TamdaOneFooterServerProps {
  locale: string
  siteId: SiteId
}

export interface NotificationCategoryPageProps {
  posts: PostData[]
  meta: Meta
  categoryName?: string
  categoryId: number
  locale: string
  currentPage: number
}

export type PermissionKey = "find" | "create" | "update" | "delete" | "restore"

export interface PermissionAction {
  enabled: boolean
}

export interface ModulePermissions {
  [K: string]: any
}

export interface RoleItem {
  id: number
  name: string
  code: string
  description?: string
  permissions: Record<string, ModulePermissions>
  createdAt: string
  updatedAt: string
  nb_users?: number
}

export interface RoleFormData {
  name: string
  description: string
}

export interface RolePermissionAction {
  enabled: boolean
}

export type RolePermissionController = {
  [action: string]: RolePermissionAction
}

export interface RolePermissionModule {
  controllers: {
    [controller: string]: RolePermissionController
  }
}

export type RolePermissions = {
  [moduleKey: string]: RolePermissionModule
}

export interface StrapiPagination {
  page?: number
  pageSize?: number
}

export interface StrapiListResponse<T> {
  data: {
    id: number
    attributes: T
  }[]
  meta: {
    pagination: {
      page: number
      pageSize: number
      pageCount: number
      total: number
    }
  }
}
export interface CareerFormAttributes {
  fullname: string
  phone: string
  email: string
  selfInstruction?: string | null
  resolved: boolean
  domain: string | null
  createdAt: string
  updatedAt: string
  publishedAt: string | null
}

export interface CareerFormItem {
  id: number
  attributes: CareerFormAttributes
}

export interface CareerFormTableItem extends CareerFormAttributes {
  id: number
}

export interface CareerFormPagination {
  page: number
  pageSize: number
  total: number
  pageCount: number
}
export interface TableSorterParams {
  field?: string
  order?: "ascend" | "descend"
}

export type CareerFormSearchParams = Record<string, string>

export type CareerFormBulkAction = "" | "delete"
export interface CareerFormFilterValues {
  fullname?: string
  email?: string
  phone?: string
  domain?: string
  publicationState?: string
}
export interface CareerFormSortPagination {
  page?: number
  pageSize?: number
  sort?: string
}
export interface CareerFormSortParams {
  sort?: string
}
export interface CareerFormDetailResponse {
  data: CareerFormItem
}

export interface CareerFormListResponse {
  data: CareerFormItem[]
  meta: {
    pagination: PaginationMeta
  }
}

export interface CareerFormStrapiParams {
  populate?: "*"
  publicationState?: string
  pagination?: {
    page: number
    pageSize: number
    withCount?: boolean
  }
  sort?: string
  filters?: {
    fullname?: { $containsi: string }
    email?: { $containsi: string }
    phone?: { $containsi: string }
    domain?: { $containsi: string }
    publishedAt?: { $null: boolean } | { $notNull: boolean }
  }
}

export interface CareerFormQueryParams
  extends CareerFormFilterValues, CareerFormSortPagination, CareerFormSortParams, CareerFormStrapiParams {}

export interface CareerFormData {
  id?: number
  fullname: string
  phone: string
  email: string
  selfInstruction?: string
  cv?: (string | number)[]
  resolved?: boolean
  domain?: string
  career?: string | number
}

export interface CareerFormUpsertPayload {
  data: CareerFormData
}

export interface TamdaLocalizationData {
  id: number
  locale: string
  [key: string]: any
}

export interface TamdaLocalizations {
  data: TamdaLocalizationData[]
}

export interface TamdaGroupAttributes {
  GoogleAnalyticsTrackingID: string | null
  address: string
  bccToEmail: string | null
  ccToEmail: string | null
  copyright: string
  createdAt: string
  email: string
  facebookUrl: string
  googleMapUrl: string | null
  hotline: string
  locale: string
  localizations: TamdaLocalizations
  twitterUrl: string
  updatedAt: string
  youtubeUrl: string
}

export interface TamdaGroupSettingResponse {
  id: number
  attributes: TamdaGroupAttributes
}
export interface CampaignAttributes {
  name: string
  dateFrom: string
  dateTo: string
  tamdaExpressCampaign?: boolean
  relatedProducts?: string
  createdAt: string
  updatedAt: string
  publishedAt: string
}

export interface CampaignData {
  id: number
  attributes: CampaignAttributes
}

export interface CampaignAttributes {
  name: string
  dateFrom: string
  dateTo: string
  tamdaExpressCampaign?: boolean
  relatedProducts?: string
  createdAt: string
  updatedAt: string
  publishedAt: string
  // BỔ SUNG CÁC TRƯỜNG QUAN HỆ
  tamdaStores?: { data: any[] }
  posts?: { data: any[] }
  campaign_subscribers?: { data: any[] }
}
export interface CampaignFieldType {
  name: string
  dateFrom: string
  dateTo: string
  tamdaExpressCampaign?: boolean
  relatedProducts?: string
  tamdaStores?: (number | string)[]
  posts?: (number | string)[]
  campaign_subscribers?: (number | string)[]
}
export interface CampaignPayload {
  data: {
    name: string
    dateFrom: string
    dateTo: string
    tamdaExpressCampaign?: boolean
    relatedProducts?: string
    tamdaStores?: (number | string)[]
    posts?: (number | string)[]
    campaign_subscribers?: (number | string)[]
    publishedAt?: string | null
  }
}

export interface CampaignSubscriberAttributes {
  fullname: string
  phone: string
  email: string
  invoiceNumber?: string | null
  branch: string
  purchasingDate: string
  resolved?: boolean | null
  createdAt: string
  updatedAt: string
  campaign?: {
    data: CampaignData | null
  }
}

export interface CampaignSubscriberData {
  id: number
  attributes: CampaignSubscriberAttributes
}

export interface ContactFormData {
  id: number
  attributes: ContactFormAttributes
}

export interface ContactFormAttributes {
  name: string
  company?: string
  phone: string
  message?: string
  email: string
  resolved: boolean
  domain: string
  createdAt: string
  updatedAt: string
  publishedAt: string | null
}

export interface ContactFormFieldType {
  name: string
  company?: string
  phone: string
  message?: string
  email: string
  resolved: boolean
  domain: string
  publishedAt?: string | null
}

export interface ContactFormPayload {
  data: {
    name: string
    company?: string
    phone: string
    message?: string
    email: string
    resolved: boolean
    domain: string
    publishedAt?: string | null
  }
}

export interface ContactFormSearchParams {
  name?: string
  email?: string
  phone?: string
  domain?: string
  page?: string
  pageSize?: string
  sort?: string
  publicationState?: string
}

export interface ContactFormFilterValues {
  name?: string
  email?: string
  phone?: string
  domain?: string
}

export interface ContactFormSortPagination {
  page?: string
  pageSize?: string
  sort?: string
}
export interface CampaignSubscriberFieldType {
  fullname: string
  phone: string
  email: string
  invoiceNumber?: string
  branch: string
  purchasingDate: string
  resolved?: boolean
  campaign?: number
}
export interface CampaignSubscriberPayload {
  data: {
    fullname: string
    phone: string
    email: string
    invoiceNumber?: string
    branch: string
    purchasingDate: string
    resolved: boolean
    campaign?: number
  }
}
export interface CampaignSubscriberFilterValues {
  search?: string
  fullname?: string
  email?: string
  phone?: string
  [key: string]: string | number | boolean | undefined
}

export interface CampaignSubscriberSortPagination {
  page?: string | number
  pageSize?: string | number
  sort?: string
}
export interface CampaignSubscriberUpsertFormProps {
  form: FormInstance
  campaignOptions: { label: string; value: number }[]
  onFormChange: () => void
}

// --- EVENT SUBSCRIBER TYPES ---

export interface EventAttributes {
  name: string
  title?: string
  slug?: string
  dateFrom?: string
  dateTo?: string
  createdAt: string
  updatedAt: string
  publishedAt: string
}

export interface EventData {
  id: number
  attributes: EventAttributes
}

export interface EventSubscriberAttributes {
  name: string
  email: string
  phone: string
  type: EventSubscriberType
  resolved?: boolean | null
  createdAt: string
  updatedAt: string
  event?: {
    data: EventData | null
  }
}

export interface EventSubscriberData {
  id: number
  attributes: EventSubscriberAttributes
}

export interface EventRelationData {
  data: EventData | null
}

export interface EventSubscriberFieldType {
  name: string
  email: string
  phone: string
  type: EventSubscriberType
  resolved?: boolean
  event?: number | string | EventRelationData
}

export interface EventSubscriberPayload {
  data: {
    name: string
    email: string
    phone: string
    type: EventSubscriberType
    resolved: boolean
    event?: string | number | null
  }
}

export interface EventSubscriberFilterValues {
  search?: string
  name?: string
  email?: string
  phone?: string
  [key: string]: string | number | boolean | undefined
}

export interface EventSubscriberSortPagination {
  page?: string | number
  pageSize?: string | number
  sort?: string
}

export interface EventSubscriberUpsertFormProps {
  form: FormInstance
  eventOptions: { label: string; value: number }[]
  onFormChange: () => void
}

export enum EventSubscriberType {
  EVENT = "event",
  NEWSLETTER = "newsletter",
  OTHER = "other",
}

export interface DefaultPermissionStructure {
  [key: string]: {
    controllers: {
      [controller: string]: string[]
    }
  }
}
export interface TamdaMediaSettingsRequest {
  data: {
    phoneNumber: string
    tagline: string
    facebook?: string
    twitter?: string
    linkedIn?: string
    hotline?: string
    email?: string
    advertisingEmail?: string
    /** @example "string or id" */
    featuredNewsId?: number | string
    /** @example "string or id" */
    hotNewsId?: number | string
    headerScripts?: string
    GoogleAnalyticsTrackingID?: string
    youtube?: string
    websiteJsonStructure?: string
    facebookAppID?: string
    instagram?: string
    tiktok?: string
    websiteInfor?: {
      websiteName?: string
      websiteKeyworlds?: string
      websiteDescription?: string
    }
  }
  websiteMetaImage?: number
  websiteFavicon?: number
  marketingInfor?: {
    displayTime?: number
    closeTime?: number
    content?: string
    image?: number[]
  }
  popupInfor?: {
    displayTime?: number
    closeTime?: number
    model_select?: number
  }
}

export interface PostLocalizationPayload {
  title: string | undefined
  slug: string
  content?: string
  description?: string
  excerpt?: string
  name?: string
  locale: string
  publishedAt?: string | null
  author?: string | number | null
  image?: string | number | null
  cover?: string | number | null
  categories?: number[]
  tags?: number[]
  websites?: number[]
  isLiveNews?: boolean
  isLiveHappening?: boolean
  isUseUIBuilder?: boolean
  blockContent?: string | number
  views?: number
  old_id?: number
  campaign?: string | number
  liveNewsItem?: Array<{
    id?: number
    title: string
    date: string
    detail: string
  }>
  seo?: {
    id?: number
    metaTitle?: string
    metaDescription?: string
    keywords?: string
    canonicalURL?: string
    metaRobots?: string
    metaViewport?: string
    preventIndexing?: boolean
    structuredData?: any
    metaImage?: any
    metaSocial?: Array<{
      id?: number
      socialNetwork: "Facebook" | "Twitter"
      title: string
      description: string
      image?: any
    }>
  }
  status?: string
}

// export interface CategoryLocalizationPayload {
//   name: string
//   slug: string
//   domain: string
//   videos?: (string | number)[]
//   playlisturl?: string
//   type: string
//   old_id?: string
//   description?: string
//   categories?: (string | number)[]
//   parents?: (string | number)[]
//   posts?: (string | number)[]
//   locale: string
//   publishedat?: string | null
//   seo?: {
//     id?: number
//     metatitle?: string
//     metadescription?: string
//     preventindexing?: boolean
//     structureddata?: string
//     keywords?: string
//     canonicalurl?: string
//     metarobots?: string
//     metaviewport?: string
//     metaimage?: any
//     metasocial?: Array<{
//       id?: number
//       socialnetwork: string
//       title: string
//       description: string
//       image?: any
//     }>
//   }
// }

// export interface CategoryAttributesExtended extends CategoryAttributes {
//   locale?: string
//   localizations?: {
//     data: CategoryData[]
//   }
// }

// export interface CategoryDataExtended {
//   id: number
//   attributes: CategoryAttributesExtended
// }

// export interface UseCategoryReturnExtended extends UseCategoryReturn {
//   handletranslatecategory: (
//     values: any,
//     targetlocale: string,
//     originalid: number
//   ) => Promise<{ success: boolean }>
// }

export interface CategoryLocalizationPayload {
  name: string
  slug: string
  domain: string
  videos?: (string | number)[]
  playlistUrl?: string
  type: string
  old_id?: string
  description?: string
  categories?: (string | number)[]
  parents?: (string | number)[]
  posts?: (string | number)[]
  locale: string
  publishedAt?: string | null
  seo?: {
    id?: number
    metaTitle?: string
    metaDescription?: string
    preventIndexing?: boolean
    structuredData?: string
    keywords?: string
    canonicalURL?: string
    metaRobots?: string
    metaViewport?: string
    metaImage?: any
    metaSocial?: Array<{
      id?: number
      socialNetwork: string
      title: string
      description: string
      image?: any
    }>
  }
}

export interface CategoryAttributesWithLocale extends CategoryAttributes {
  locale?: string
  localizations?: {
    data: CategoryData[]
  }
}

export interface CategoryDataWithLocale extends Omit<CategoryData, "attributes"> {
  attributes: CategoryAttributesWithLocale
}

export interface UseCategoryReturn {
  categories: CategoryData[]
  category: CategoryData | null
  loading: boolean
  error: string | null
  pagination: Pagination
  // fetchCategories: (searchParams?: { [key: string]: string }, publicationState?: string) => Promise<any>
  fetchCategoryById: (id: number) => Promise<CategoryData | null>
  createCategory: (data: AttributesCategory) => Promise<boolean>
  updateCategory: (id: string, data: AttributesCategory) => Promise<boolean>
  deleteCategory: (id: number) => Promise<boolean>
  duplicateCategory: (id: number) => Promise<boolean>
  resolveLocalizationTarget: (record: CategoryData, locale: string) => Promise<number | undefined>
  handleTableChange: (pagination: TablePaginationConfig, filters?: unknown, sorter?: unknown) => void
  handleDelete: (id: number) => void
  permissions: Record<string, any> | null
  hasPermission: (action: "create" | "update" | "delete") => boolean
  handleTranslateCategory: (values: any, targetLocale: string, originalId: number) => Promise<{ success: boolean }>
}

export interface CategoryAttributesWithLocale extends CategoryAttributes {
  locale?: string
  localizations?: {
    data: CategoryData[]
  }
}

export interface CategoryDataWithLocale extends Omit<CategoryData, "attributes"> {
  attributes: CategoryAttributesWithLocale
}

export interface CategoryAsPostAttributes extends CategoryAttributesWithLocale {
  title: string
  content: string
  excerpt: string
  readingTime?: number
  shortDescription?: string
  views: number
  old_id: number | null
  isLiveHappening: boolean | null
  isLiveNews: boolean | null
  liveNewsItem: LiveNewsItemData[]
  seo: SeoData
  categories: { data: CategoryData[] }
  tags: { data: TagData[] }
  image: { data?: DataImage | null }
  blockContent: { data?: BlockContent | null }
}

export interface CategoryAsPostData extends Omit<PostData, "attributes"> {
  id: number
  attributes: CategoryAsPostAttributes
}

export interface CareerAttributes {
  position: string
  company?: string
  location?: string
  jobType: "fulltime" | "parttime" | "remote"
  branch: string
  salary?: string
  quantity?: number
  domain?: string
  expiredDate?: string
  detail?: string
  content?: string // Tương đương với detail tùy cấu trúc API
  locale: string
  createdAt: string
  updatedAt: string
  publishedAt?: string
  image?: { data: MediaData | null }
  seo?: CareerSeoData
  localizations?: { data: CareerData[] }
}

export interface CareerSeoData {
  id?: number
  metaTitle?: string
  metaDescription?: string
  keywords?: string
}

export interface CareerData {
  id: number
  attributes: CareerAttributes
}

export interface CareerResponseDataObject {
  id: number
  attributes: CareerAttributes
}

export interface CareerFormValues {
  position: string
  company?: string
  location?: string
  jobType: string
  branch: string
  expiredDate?: string
  content?: string
  metaTitle?: string
  metaDescription?: string
  keywords?: string
}

export interface CareerLocalizationRequest {
  position: string
  jobType: "fulltime" | "parttime" | "remote"
  locale: string
  company?: string
  location?: string
  branch: "brno" | "praha" | "usti" | undefined
  expiredDate?: string
  detail?: string
}

export type PostWorkflowStatus = "draft" | "pending" | "approved" | "published" | "rejected" | "scheduled"
